/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { Activity, BarChart3, Blocks, BookMarked, Building2, CalendarClock, CheckCircle2, ChevronLeft, ClipboardList, Copy, CopyX, DatabaseZap, FileText, FlaskConical, HeartHandshake, Inbox as InboxIcon, KeyRound, Landmark, LayoutTemplate, Lightbulb, LogOut, MessageSquareText, Search, Send, Settings, ShieldCheck, SlidersHorizontal, Stethoscope, Telescope, type LucideIcon, UserCheck, Users, Workflow } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router";
import { backHref } from "../lib/navigation";
import { useAuth } from "../auth/AuthContext";
import { ChromeProvider, useChromeState } from "../contexts/ChromeContext";
import { useDemoEnabled } from "../lib/envConfig";
import { Logo } from "../components/ui/Logo";
import { ManateeMark } from "../components/ui/ManateeMark";
import { FaxIcon } from "../components/ui/FaxIcon";
import { Modal } from "../components/ui/Modal";
import { UserMenu } from "../components/UserMenu";
import { cn } from "../lib/utils";
import { CARE_STAFF_ROLES } from "../api/accounts";
import { gapCount, insuranceGaps } from "../api/insurance";

const INSURANCE_PATH = "/care/insurance";

type NavIcon = LucideIcon | typeof FaxIcon;

type NavLeaf = {
  to: string;
  label: string;
  icon: NavIcon;
  demoOnly?: boolean;
  /** Narrows the group's roles for this item; every role of the group when absent. */
  roles?: string[];
};

type NavGroup = {
  id: string;
  label: string;
  icon: NavIcon;
  /** Path prefixes that mark this group active. The first item's `to` is the group's landing. */
  match: string[];
  roles: string[];
  items: NavLeaf[];
};

const GROUPS: NavGroup[] = [
  {
    id: "inbox",
    label: "Inbox",
    icon: InboxIcon,
    match: ["/inbox"],
    roles: ["Admin", "Support"],
    items: [
      { to: "/inbox", label: "Open", icon: InboxIcon },
      { to: "/inbox/claimed", label: "Claimed", icon: UserCheck },
      { to: "/inbox/resolved", label: "Resolved", icon: CheckCircle2 },
    ],
  },
  {
    id: "agents",
    label: "Agents",
    icon: Workflow,
    match: ["/agents", "/runs", "/streams"],
    roles: ["Admin", "Support"],
    items: [
      { to: "/agents/runs", label: "Runs", icon: Activity },
      { to: "/agents/scheduling", label: "Scheduling", icon: CalendarClock },
      { to: "/agents/manual-run", label: "Run the agent", icon: Send },
    ],
  },
  {
    id: "care",
    label: "Care",
    icon: HeartHandshake,
    match: ["/care", "/accounts"],
    roles: [...CARE_STAFF_ROLES],
    items: [
      { to: "/care/families", label: "Families", icon: Users },
      { to: "/care/duplicates", label: "Duplicates", icon: Copy, roles: ["Admin", "Support"] },
      { to: "/care/team", label: "Care team", icon: Stethoscope },
      { to: INSURANCE_PATH, label: "Insurance", icon: Landmark },
    ],
  },
  {
    id: "referrals",
    label: "Referrals",
    icon: Building2,
    match: ["/referrals"],
    roles: ["Admin", "Support", "CareCoordinator"],
    items: [
      { to: "/referrals", label: "Referrals", icon: ClipboardList },
      { to: "/referrals/faxes", label: "Fax inbox", icon: FaxIcon },
      { to: "/referrals/organizations", label: "Organizations", icon: Building2 },
      { to: "/referrals/providers", label: "Providers", icon: Stethoscope },
      { to: "/referrals/duplicates", label: "Duplicates", icon: CopyX },
    ],
  },
  {
    id: "intelligence",
    label: "Intelligence",
    icon: Lightbulb,
    match: ["/intelligence"],
    roles: ["Admin", "Support"],
    items: [
      { to: "/intelligence/insights", label: "My Insights", icon: Telescope },
      { to: "/intelligence/catalog", label: "Catalog", icon: BookMarked },
      { to: "/intelligence/reports", label: "Reports", icon: BarChart3 },
    ],
  },
  {
    id: "build",
    label: "Build",
    icon: Blocks,
    match: ["/build"],
    roles: ["Admin", "Support"],
    items: [
      { to: "/build/forms", label: "Forms", icon: FileText },
      { to: "/build/templates", label: "Templates", icon: MessageSquareText },
      { to: "/build/content", label: "Content", icon: LayoutTemplate },
    ],
  },
  {
    id: "admin",
    label: "Admin",
    icon: Settings,
    match: ["/admin"],
    roles: ["Admin"],
    items: [
      { to: "/admin/guardrails", label: "Guardrails", icon: ShieldCheck },
      { to: "/admin/demo", label: "Demo", icon: FlaskConical, demoOnly: true },
      { to: "/admin/policies", label: "Policies", icon: SlidersHorizontal },
      { to: "/admin/sync", label: "Data sync", icon: DatabaseZap },
      { to: "/admin/access", label: "Access", icon: KeyRound },
    ],
  },
];

function matchesGroup(group: NavGroup, pathname: string): boolean {
  return group.match.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

export function Shell() {
  return (
    <ChromeProvider>
      <ShellInner />
    </ChromeProvider>
  );
}

function ShellInner() {
  const { hasRole, logout } = useAuth();
  const chrome = useChromeState();
  const location = useLocation();
  const { pathname } = location;
  const [searchOpen, setSearchOpen] = useState(false);

  const demoEnabled = useDemoEnabled();
  const groups = GROUPS.filter((group) => group.roles.some(hasRole)).map((group) => ({
    ...group,
    items: group.items.filter(
      (item) =>
        (!item.demoOnly || demoEnabled) && (item.roles === undefined || item.roles.some(hasRole)),
    ),
  }));
  const activeGroup = groups.find((group) => matchesGroup(group, pathname)) ?? groups[0];
  const insuranceGapCount = useInsuranceGapCount(
    CARE_STAFF_ROLES.some(hasRole),
    pathname.startsWith(INSURANCE_PATH),
  );

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      const target = event.target as HTMLElement | null;
      const typing =
        target !== null &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable);
      if (event.key.toLowerCase() === "k" && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setSearchOpen(true);
      } else if (event.key === "k" && !typing && !event.metaKey && !event.ctrlKey && !event.altKey) {
        event.preventDefault();
        setSearchOpen(true);
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="relative flex h-dvh flex-col lg:flex-row">
      <div className="app-bg" aria-hidden />

      {/* Mobile top bar */}
      <header className="glass sticky top-0 z-40 flex items-center justify-between rounded-none border-x-0 border-t-0 px-4 py-2.5 lg:hidden">
        {chrome.back ? (
          <Link
            to={backHref(chrome.back.to, location.state)}
            className="inline-flex items-center gap-1 text-sm font-medium text-text-secondary"
          >
            <ChevronLeft size={16} />
            {chrome.back.label}
          </Link>
        ) : (
          <Logo className="h-5 text-text-primary" />
        )}
        <UserMenu placement="down" />
      </header>

      <div className="flex min-h-0 w-full flex-1">
        {/* Desktop sidebar: one frosted surface — an icon column for groups and a plain
            items column for the active group, divided by a hairline. */}
        <div className="glass-strong z-30 hidden shrink-0 overflow-hidden rounded-none border-y-0 border-l-0 shadow-[4px_0_24px_-10px_rgba(15,23,42,0.25)] lg:flex">
          <nav
            aria-label="Sections"
            className="flex w-16 flex-col items-center gap-1.5 border-r border-border/70 px-2 py-3"
          >
            <Link to="/inbox" className="mb-2 grid size-8 place-items-center" aria-label="Manatee">
              <ManateeMark className="h-6 text-text-primary" />
            </Link>
            {groups.map((group) => {
              const isActive = group.id === activeGroup?.id;
              const GroupIcon = group.icon;
              return (
                <Link
                  key={group.id}
                  to={group.items[0]?.to ?? "/inbox"}
                  aria-label={group.label}
                  aria-current={isActive ? "page" : undefined}
                  className={cn(
                    "group/rail relative grid size-10 place-items-center rounded-lg transition-all duration-200",
                    isActive
                      ? "bg-accent/10 text-accent"
                      : "text-text-tertiary hover:bg-bg-hover/70 hover:text-text-primary",
                  )}
                >
                  {isActive && (
                    <span className="absolute -left-1.5 top-1/2 h-6 w-1 -translate-y-1/2 rounded-full bg-accent shadow-[0_0_12px_2px_color-mix(in_srgb,var(--color-accent)_55%,transparent)]" />
                  )}
                  <GroupIcon size={20} strokeWidth={isActive ? 2.4 : 2} />
                  <span className="tooltip-surface pointer-events-none absolute left-[calc(100%+0.85rem)] z-50 whitespace-nowrap rounded-lg px-2.5 py-1 text-xs font-medium opacity-0 transition-opacity duration-150 group-hover/rail:opacity-100">
                    {group.label}
                  </span>
                </Link>
              );
            })}
            <button
              type="button"
              onClick={() => void logout()}
              aria-label="Sign out"
              className="group/rail relative mt-auto grid size-10 place-items-center rounded-lg text-text-tertiary transition-all duration-200 hover:bg-bg-hover/70 hover:text-text-primary"
            >
              <LogOut size={20} strokeWidth={2} />
              <span className="tooltip-surface pointer-events-none absolute left-[calc(100%+0.85rem)] z-50 whitespace-nowrap rounded-lg px-2.5 py-1 text-xs font-medium opacity-0 transition-opacity duration-150 group-hover/rail:opacity-100">
                Sign out
              </span>
            </button>
          </nav>

          {activeGroup && (
            <div className="flex w-52 flex-col px-3 py-3">
              <div className="px-2 pb-3 pt-0.5">
                <span className="font-display text-lg leading-none text-text-primary">
                  {activeGroup.label}
                </span>
              </div>
              <nav className="flex flex-1 flex-col gap-1">
                {activeGroup.items.map(({ to, label, icon: ItemIcon }) => (
                  <NavLink
                    key={to}
                    to={to}
                    end={to === "/inbox" || to === "/referrals"}
                    className={({ isActive }) =>
                      cn(
                        "group relative flex items-center gap-2.5 rounded-lg px-2 py-2 text-sm transition-all duration-200",
                        isActive
                          ? "bg-accent/15 font-medium text-accent ring-1 ring-accent/25"
                          : "text-text-secondary hover:bg-bg-hover/70 hover:text-text-primary",
                      )
                    }
                  >
                    <ItemIcon size={16} strokeWidth={2} />
                    {label}
                    {to === INSURANCE_PATH && insuranceGapCount > 0 && (
                      <span
                        aria-label={`${insuranceGapCount} need mapping`}
                        className="ml-auto rounded-full bg-warning-bg px-1.5 text-[11px] font-medium text-warning"
                      >
                        {insuranceGapCount}
                      </span>
                    )}
                  </NavLink>
                ))}
              </nav>
            </div>
          )}
        </div>

        {/* Content column: header + body */}
        <div className="flex min-w-0 flex-1 flex-col">
          <header className="sticky top-0 z-20 hidden items-center justify-between gap-4 px-6 py-3 backdrop-blur-xl lg:flex">
            <div className="flex min-w-0 items-center gap-2.5">
              {chrome.back && (
                <Link
                  to={backHref(chrome.back.to, location.state)}
                  className="inline-flex items-center gap-1 rounded-lg px-1.5 py-1 text-sm text-text-secondary transition-colors hover:text-accent"
                >
                  <ChevronLeft size={16} />
                  {chrome.back.label}
                </Link>
              )}
              {chrome.back && chrome.title && (
                <>
                  <span className="text-text-tertiary" aria-hidden>
                    /
                  </span>
                  <div className="truncate text-sm font-medium text-text-primary">
                    {chrome.title}
                  </div>
                </>
              )}
            </div>
            <div className="flex items-center gap-3">
              <SearchTrigger onOpen={() => setSearchOpen(true)} />
              <UserMenu placement="down" />
            </div>
          </header>

          <div className="flex min-h-0 flex-1">
            <main
              className={cn(
                "min-w-0 flex-1 overflow-y-auto px-4 pt-5 sm:px-6 lg:px-8 lg:pb-8",
                chrome.hideBottomNav ? "pb-8" : "pb-28",
              )}
            >
              <Outlet />
            </main>
            <SidePanel content={chrome.sidePanel} />
          </div>
        </div>
      </div>

      <Modal isOpen={searchOpen} onClose={() => setSearchOpen(false)} title="Search" size="lg">
        <div className="space-y-4 px-6 py-5">
          <div className="glass flex items-center gap-3 rounded-2xl p-2 pl-4">
            <Search size={16} className="shrink-0 text-text-tertiary" />
            <input
              autoFocus
              placeholder="Search families, runs, payers…"
              className="min-w-0 flex-1 bg-transparent py-2 text-[15px] text-text-primary outline-none placeholder:text-text-tertiary"
            />
          </div>
          <p className="py-6 text-center text-sm text-text-tertiary">
            Unified search is coming soon. For now, use the navigation on the left.
          </p>
        </div>
      </Modal>

      {/* Mobile bottom tab bar: groups */}
      {!chrome.hideBottomNav && (
        <nav className="glass-strong fixed inset-x-3 bottom-3 z-40 rounded-3xl lg:hidden">
          <div
            className="mx-auto flex max-w-md items-stretch justify-around px-1 pt-1.5"
            style={{ paddingBottom: "max(env(safe-area-inset-bottom), 0.5rem)" }}
          >
            {groups.map((group) => {
              const isActive = group.id === activeGroup?.id;
              const GroupIcon = group.icon;
              return (
                <Link
                  key={group.id}
                  to={group.items[0]?.to ?? "/inbox"}
                  aria-current={isActive ? "page" : undefined}
                  className={cn(
                    "flex min-w-12 flex-col items-center gap-0.5 rounded-2xl px-2 py-1.5 text-[10px] font-medium transition-colors",
                    isActive ? "text-accent" : "text-text-tertiary hover:text-text-secondary",
                  )}
                >
                  <GroupIcon size={20} strokeWidth={isActive ? 2.5 : 2} />
                  {group.label}
                </Link>
              );
            })}
          </div>
        </nav>
      )}
    </div>
  );
}

/**
 * How many insurance tags and plans need mapping, for the nav badge. Re-read on entering and on
 * leaving the Insurance page, so a mapping made there shows in the badge without a reload.
 */
function useInsuranceGapCount(allowed: boolean, onInsurancePage: boolean): number {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!allowed) {
      return;
    }
    let cancelled = false;
    void insuranceGaps().then((result) => {
      if (!cancelled && result.ok) {
        setCount(gapCount(result.value));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [allowed, onInsurancePage]);
  return count;
}

function SearchTrigger({ onOpen }: { onOpen: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label="Search"
      className="flex w-56 items-center gap-2 rounded-2xl border border-border bg-bg-elevated/50 py-1.5 pl-3 pr-2 text-sm text-text-tertiary transition-colors hover:border-border-hover hover:text-text-secondary xl:w-64"
    >
      <Search size={15} className="shrink-0" />
      <span className="flex-1 text-left">Search</span>
      <kbd className="rounded-md border border-border bg-bg-hover px-1.5 py-0.5 font-mono text-[10px] font-medium leading-none text-text-tertiary">
        K
      </kbd>
    </button>
  );
}

function SidePanel({ content }: { content: React.ReactNode }) {
  if (!content) {
    return null;
  }
  return (
    <aside className="hidden w-80 shrink-0 overflow-y-auto px-4 py-5 2xl:w-96 xl:block">
      {content}
    </aside>
  );
}
